import {
  ShieldCheck, Building2, Users, KeyRound, Activity, LifeBuoy, ServerCog, UserPlus, History, MapPin,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Card, CardHeader, CardBody, Badge, PageHeader, EmptyState, Loading, AccessDenied,
  Button, Alert, TextInput,
} from "@/components/redvolt/kit";
import { fmtDate, useLoader, apiError } from "@/app/federation/common";

const DesignateForm = ({ onSubmit }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    await onSubmit(email.trim(), () => setEmail(""));
    setBusy(false);
  };
  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Designate an administrator</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <TextInput
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="person@example.com"
          className="flex-1"
          data-testid="designate-admin-email"
        />
        <Button size="sm" onClick={submit} disabled={busy || !email.trim()} data-testid="designate-admin-submit">
          <UserPlus className="mr-1.5 h-4 w-4" /> {busy ? "Adding…" : "Add administrator"}
        </Button>
      </div>
      <p className="mt-2 text-xs text-slate-400">The person must have signed in to RedVolt at least once. You can revoke them at any time.</p>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
    <span className="grid h-10 w-10 place-items-center rounded-lg bg-accent text-primary"><Icon className="h-5 w-5" /></span>
    <div>
      <p className="text-2xl font-bold text-slate-900" data-testid={`stat-${label}`}>{value}</p>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
    </div>
  </div>
);

export default function PlatformAdminConsole({ api, me }) {
  const isAdmin = me?.user?.platform_role === "platform_admin";
  const statusQ = useLoader(async () => (await api.get("/platform/status")).data, [isAdmin]);
  const orgsQ = useLoader(async () => (await api.get("/platform/organizations")).data.organizations, [isAdmin]);
  const adminsQ = useLoader(async () => (await api.get("/platform/admins")).data.admins, [isAdmin]);
  const grantsQ = useLoader(async () => (await api.get("/platform/support-grants")).data.grants, [isAdmin]);
  const activityQ = useLoader(async () => (await api.get("/platform/activity")).data.activity, [isAdmin]);
  const reportQ = useLoader(async () => (await api.get("/platform/organizations-report")).data, [isAdmin]);

  if (!isAdmin) return <AccessDenied message="This area is for RedVolt platform administrators." />;
  const st = statusQ.data;

  const revoke = async (id) => {
    try { await api.post(`/platform/admins/${id}/revoke`); adminsQ.reload(); activityQ.reload(); }
    catch (e) { apiError(e, "Could not revoke"); }
  };

  const designate = async (email, done) => {
    try {
      const r = await api.post("/platform/admins", { email });
      toast.success(r.data.already ? "Already a platform administrator" : `${r.data.email} is now a platform administrator`);
      adminsQ.reload();
      activityQ.reload();
      done();
    } catch (e) {
      if (e?.response?.status === 404) apiError(e, "No RedVolt user with that email — they must sign in once first");
      else apiError(e, "Could not designate administrator");
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-admin-console">
      <PageHeader title="RedVolt Administration"
        subtitle="Platform-level operations. This context is separate from any organization." />

      <Alert variant="info">
        Platform administration manages the platform itself. It does not grant access to organizations’ private data — that still requires normal membership or an audited support (break-glass) grant.
      </Alert>

      {/* Status */}
      {statusQ.loading ? <Loading label="Loading platform status…" /> : st && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Building2} label="Organizations" value={st.counts.organizations} />
          <Stat icon={Users} label="Users" value={st.counts.users} />
          <Stat icon={LifeBuoy} label="Support Grants" value={st.counts.active_support_grants} />
          <Stat icon={Activity} label="Schema" value={st.schema_version ? "OK" : "—"} />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="System health" icon={ServerCog} />
          <CardBody className="pt-2 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Database</span><Badge variant="implemented">{st?.db || "…"}</Badge></div>
            <div className="h-px bg-slate-100" />
            <div className="flex justify-between"><span className="text-slate-500">Identity (WorkOS)</span><Badge variant={st?.workos_configured ? "implemented" : "vision"}>{st?.workos_configured ? "Configured" : "Not set"}</Badge></div>
            <div className="h-px bg-slate-100" />
            <div className="flex justify-between"><span className="text-slate-500">Schema version</span><span className="font-mono text-xs text-slate-700">{st?.schema_version || "—"}</span></div>
          </CardBody>
        </Card>

        {/* Platform admins */}
        <Card className="lg:col-span-2">
          <CardHeader title="Platform administrators" icon={ShieldCheck} />
          <CardBody className="pt-4">
            {adminsQ.loading ? <Loading /> : (adminsQ.data || []).length === 0 ? (
              <EmptyState icon={ShieldCheck} title="No administrators" />
            ) : (
              <div className="space-y-2" data-testid="platform-admins-list">
                {adminsQ.data.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-4 py-2.5" data-testid={`platform-admin-${a.id}`}>
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <KeyRound className="h-4 w-4 text-primary" /> {a.email}
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge variant="active">Platform Admin</Badge>
                      {a.id !== me.user.id && (
                        <Button size="sm" variant="ghost" onClick={() => revoke(a.id)} data-testid={`revoke-admin-${a.id}`}>Revoke</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DesignateForm onSubmit={designate} />
          </CardBody>
        </Card>
      </div>

      {/* Organizations */}
      <Card>
        <CardHeader title="Organizations" icon={Building2} />
        <CardBody className="pt-4">
          {orgsQ.loading ? <Loading label="Loading organizations…" /> : (orgsQ.data || []).length === 0 ? (
            <EmptyState icon={Building2} title="No organizations yet" />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-left text-sm" data-testid="platform-orgs-table">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2.5">Organization</th>
                    <th className="px-4 py-2.5">Members</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orgsQ.data.map((o) => (
                    <tr key={o.id} className="hover:bg-slate-50" data-testid={`platform-org-${o.id}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-800">{o.name}</td>
                      <td className="px-4 py-2.5 text-slate-600">{o.member_count}</td>
                      <td className="px-4 py-2.5"><Badge variant={o.status === "active" ? "implemented" : "neutral"}>{o.status}</Badge></td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Geographic distribution (aggregate location rollup) */}
      <Card>
        <CardHeader title="Geographic distribution" icon={MapPin}
          action={<span className="text-xs text-slate-400">Aggregate · organization metadata only</span>} />
        <CardBody className="pt-4">
          {reportQ.loading ? <Loading label="Loading report…" /> : !reportQ.data ? (
            <EmptyState icon={MapPin} title="No report available" />
          ) : (() => {
            const rep = reportQ.data;
            const Rollup = ({ title, rows, testid }) => (
              <div className="rounded-lg border border-slate-200 p-4" data-testid={testid}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
                {(rows || []).length === 0 ? <p className="text-sm text-slate-400">No data</p> : (
                  <div className="space-y-1.5">
                    {rows.slice(0, 8).map((r) => (
                      <div key={r.label} className="flex items-center justify-between gap-2 text-sm">
                        <span className="min-w-0 truncate text-slate-700" title={r.label}>{r.label}</span>
                        <Badge variant="neutral">{r.count}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
            return (
              <div className="space-y-4" data-testid="org-location-report">
                <div className="grid gap-3 sm:grid-cols-3">
                  <Stat icon={Building2} label="Active Orgs" value={rep.total_organizations} />
                  <Stat icon={MapPin} label="With Location" value={rep.with_location} />
                  <Stat icon={MapPin} label="Missing Location" value={rep.without_location} />
                </div>
                <div className="grid gap-3 lg:grid-cols-3">
                  <Rollup title="By state" rows={rep.by_state} testid="rollup-by-state" />
                  <Rollup title="By county / parish" rows={rep.by_county} testid="rollup-by-county" />
                  <Rollup title="By organization type" rows={rep.by_org_type} testid="rollup-by-org-type" />
                </div>
              </div>
            );
          })()}
        </CardBody>
      </Card>

      {/* Administration activity */}
      <Card>
        <CardHeader title="Administration activity" icon={History}
          action={<span className="text-xs text-slate-400">Read-only · from the audit trail</span>} />
        <CardBody className="pt-4">
          {activityQ.loading ? <Loading label="Loading activity…" /> : (activityQ.data || []).length === 0 ? (
            <EmptyState icon={History} title="No administration activity yet"
              description="Claims, designations and revocations of platform administrators will appear here." />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-left text-sm" data-testid="platform-activity-table">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Action</th>
                    <th className="px-4 py-2.5">Administrator affected</th>
                    <th className="px-4 py-2.5">By</th>
                    <th className="px-4 py-2.5">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {activityQ.data.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50" data-testid={`activity-row-${a.id}`}>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{fmtDate(a.timestamp)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={a.action === "revoked" ? "neutral" : "implemented"}>{a.action_label}</Badge>
                      </td>
                      <td className="max-w-[16rem] truncate px-4 py-2.5 font-medium text-slate-800" title={a.affected}>{a.affected}</td>
                      <td className="max-w-[16rem] truncate px-4 py-2.5 text-slate-600" title={a.actor}>{a.actor}</td>
                      <td className="px-4 py-2.5"><Badge variant="active">{a.outcome}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Support / break-glass */}
      <Card>
        <CardHeader title="Support access (break-glass)" icon={LifeBuoy}
          action={<span className="text-xs text-slate-400">Separate, audited, time-limited</span>} />
        <CardBody className="pt-4">
          {grantsQ.loading ? <Loading /> : (grantsQ.data || []).length === 0 ? (
            <EmptyState icon={LifeBuoy} title="No support grants"
              description="RedVolt staff have zero standing access. Break-glass grants are explicit, scoped, time-limited and fully audited." />
          ) : (
            <div className="space-y-2" data-testid="support-grants-list">
              {grantsQ.data.map((g) => (
                <div key={g.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm" data-testid={`support-grant-${g.id}`}>
                  <span className="min-w-0 truncate text-slate-700" title={g.tenant_id}>
                    Support access — <span className="font-medium">{g.organization_name || "Unknown organization"}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge variant={g.status === "active" ? "active" : "neutral"}>{g.status}</Badge>
                    {g.expires_at && <span className="text-xs text-slate-400">until {fmtDate(g.expires_at)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
